import { DraftWaypointSupplyStrategy, ProcedureStatus, InstructionType, ChartingStatus, Role, FeatureFlag, SupplyItemStatus } from '@caresend/types';
import { getCancelationUserTypeForReschedule, getDisallowRescheduling } from '@caresend/utils';
import { ButtonStyle } from '../../../../../../buttons/ButtonComponent/model.js';
import { extractTarget } from '../../helpers/helpers.js';
import { getCommittedWorkflow, existingWorkflowMap, getWorkflowItemInitializer, maybeAddToMap, sortWorkflowByPriority } from '../helpers.js';
import { ProcedureWorkflowContextID, ProcedureWorkflowItemID, DropOffWorkflowItemID, PackingWorkflowItemID } from './model.js';
import { WorkflowModalComponentName, WorkflowItemModalButtonAction, WorkflowInlineComponentName } from '../model.js';
import { getStore } from '../../../../../../../store/helpers/initStore.js';

const CHECK_PENDING_CHARTING_ITEMS_MODAL_VARIANT = 'showPendingChartingItemsModalVariant';
/**
 * Default context workflow items
 * - Label containers
 * - Chart procedure
 */
const getDefaultWorkflowItems = (procedure, { waypointActions, procedures }, waypointActionID) => {
    var _a, _b, _c;
    const workflow = getCommittedWorkflow(procedure, waypointActionID, ProcedureWorkflowContextID.DEFAULT);
    const workflowMap = existingWorkflowMap(workflow);
    const priorities = {};
    const waypointID = (_a = waypointActions === null || waypointActions === void 0 ? void 0 : waypointActions.getRaw(waypointActionID)) === null || _a === void 0 ? void 0 : _a.waypointID;
    let priority = 1;
    /** Initialize default procedure workflow item creator */
    const initWorkflowItem = getWorkflowItemInitializer(procedure.id, 'procedures', {
        context: ProcedureWorkflowContextID.DEFAULT,
        storeRef: 'waypointActions',
        referenceID: waypointActionID,
    });
    /** Label containers workflow item */
    const draftWaypoint = procedures === null || procedures === void 0 ? void 0 : procedures.getters.draftWaypoint(procedure);
    if ((draftWaypoint === null || draftWaypoint === void 0 ? void 0 : draftWaypoint.supplyStrategy) !== DraftWaypointSupplyStrategy.KIT_SHIPMENT) {
        const labelContainers = initWorkflowItem(ProcedureWorkflowItemID.LABEL_CONTAINERS, {
            after: {
                status: ProcedureStatus.IN_PROGRESS,
            },
            modal: {
                name: WorkflowModalComponentName.FILL_OUT_LABELS,
                title: 'Label containers',
                props: {
                    procedureID: procedure.id,
                    waypointActionID,
                    waypointID,
                    itineraryID: (_b = waypointActions === null || waypointActions === void 0 ? void 0 : waypointActions.getRaw(waypointActionID)) === null || _b === void 0 ? void 0 : _b.itineraryID,
                },
            },
        });
        priorities[labelContainers.id] = priority;
        priority += 1;
        maybeAddToMap(labelContainers, workflowMap);
    }
    /** Procedure instructions workflow item */
    if ((_c = procedure.instructions) === null || _c === void 0 ? void 0 : _c.find((instruction) => instruction.type === InstructionType.NURSE)) {
        const procedureInstructions = initWorkflowItem(ProcedureWorkflowItemID.PROCEDURE_INSTRUCTIONS, {
            after: { status: ProcedureStatus.IN_PROGRESS },
            modal: {
                name: WorkflowModalComponentName.PROCEDURE_INSTRUCTIONS,
                title: 'Procedure instructions',
                props: { procedureID: procedure.id },
                buttonProps: {
                    label: 'View procedure instructions',
                    iconRight: 'chevron-right',
                },
            },
        });
        priorities[procedureInstructions.id] = priority;
        maybeAddToMap(procedureInstructions, workflowMap);
        priority += 1;
    }
    // TODO: Reinstate Sample Level Workflow Item
    // const draftWaypoint = procedures?.getters.draftWaypoint(procedure);
    // if (draftWaypoint?.supplyStrategy !== DraftWaypointSupplyStrategy.KIT_SHIPMENT) {
    //   const sampleCharting = initWorkflowItem(
    //     ProcedureWorkflowItemID.SAMPLES, {
    //       after: {
    //         status: ProcedureStatus.COMPLETED,
    //       },
    //       component: {
    //         name: WorkflowInlineComponentName.SAMPLE_CHARTING,
    //         props: {
    //           procedureID: procedure.id,
    //           waypointActionID,
    //           waypointID: waypointID ?? '',
    //           itineraryID: waypointActions?.getRaw(waypointActionID)?.itineraryID,
    //         },
    //       },
    //     }, {
    //       key: 'samples',
    //       context: SampleWorkflowContextID.DEFAULT,
    //     });
    //   priorities[sampleCharting.id] = 2;
    //   maybeAddToMap(sampleCharting, workflowMap);
    // }
    /**
     * Chart procedure workflow item
     *
     * TODO: This is a temporary solution to get the charting workflow item.
     * Bring more in line with the new workflow item structure and paradigm
    */
    const chartProcedure = initWorkflowItem(ProcedureWorkflowItemID.PROCEDURE_CHARTING, {
        after: {
            status: ProcedureStatus.COMPLETED,
            charting: {
                status: ChartingStatus.COMPLETE,
            },
        },
        afterFailure: {
            status: ProcedureStatus.FAILED,
            charting: {
                status: ChartingStatus.COMPLETE,
            },
        },
        modal: {
            buttonProps: {
                label: 'Chart procedure',
                iconRight: 'chevron-right',
            },
            /**
             * When the user has unlabeled tubes, we want to open a warning modal
             * to inform them that they need to label the tubes before charting
             *
             * button should look exactly the same as the default
             */
            modalMetaVariants: {
                [CHECK_PENDING_CHARTING_ITEMS_MODAL_VARIANT]: {
                    footerButtons: [
                        {
                            label: 'Submit charting',
                            action: WorkflowItemModalButtonAction.TRIGGER_EXPOSED_ACTION,
                        },
                    ],
                },
            },
            name: WorkflowModalComponentName.PROCEDURE_CHARTING,
            title: 'Chart procedure',
            props: {
                procedureID: procedure.id,
                waypointActionID,
            },
        },
    });
    priorities[chartProcedure.id] = priority;
    maybeAddToMap(chartProcedure, workflowMap);
    return sortWorkflowByPriority(Array.from(workflowMap.values()), priorities);
};
const getDefaultFailureWorkflowItems = (procedure, { procedures: _ }, waypointActionID) => {
    const workflow = getCommittedWorkflow(procedure, waypointActionID, ProcedureWorkflowContextID.DEFAULT_FAILURE);
    const workflowMap = existingWorkflowMap(workflow);
    const priorities = {};
    const initWorkflowItem = getWorkflowItemInitializer(procedure.id, 'procedures', {
        context: ProcedureWorkflowContextID.DEFAULT_FAILURE,
        storeRef: 'waypointActions',
        referenceID: waypointActionID,
    });
    const chartingFailure = initWorkflowItem(ProcedureWorkflowItemID.CHARTING_FAILURE, {
        afterFailure: {
            status: ProcedureStatus.FAILED,
            disallowRescheduling: (() => {
                var _a, _b;
                const store = getStore();
                const { role = Role.NURSE } = (_a = store.state.auth.user) !== null && _a !== void 0 ? _a : {};
                const { type } = (_b = store.state.office.office) !== null && _b !== void 0 ? _b : {};
                const userType = getCancelationUserTypeForReschedule(role);
                return getDisallowRescheduling(role, userType, type);
            })(),
        },
        component: {
            name: WorkflowInlineComponentName.TODO,
            props: {
                text: 'Charting failure',
            },
        },
    });
    priorities[chartingFailure.id] = 1;
    maybeAddToMap(chartingFailure, workflowMap);
    return sortWorkflowByPriority(Array.from(workflowMap.values()), priorities);
};
/**
 * Drop off samples workflow items
 * - Scan specimen bag
 * - Present requisition form / Drop off samples
 */
const getDropOffWorkflowItems = (procedure, { procedures }, waypointActionID) => {
    const workflow = getCommittedWorkflow(procedure, waypointActionID, ProcedureWorkflowContextID.DROP_OFF_SAMPLES);
    const workflowMap = existingWorkflowMap(workflow);
    const priorities = {};
    /** Initialize drop off samples procedure workflow item creator */
    const initWorkflowItem = getWorkflowItemInitializer(procedure.id, 'procedures', {
        context: ProcedureWorkflowContextID.DROP_OFF_SAMPLES,
        storeRef: 'waypointActions',
        referenceID: waypointActionID,
    });
    /** Scan specimen bag workflow item */
    const scanSpecimenBag = initWorkflowItem(DropOffWorkflowItemID.SCAN_SPECIMEN_BAG, {
        after: {
            specimenBagScanned: true,
        },
        modal: {
            name: WorkflowModalComponentName.SCANNER,
            buttonProps: {
                label: 'Unlock',
                iconRight: 'unlock',
                buttonStyle: ButtonStyle.OUTLINE,
            },
            props: {
                recordManagerID: 'procedures',
                recordManagerTargetID: procedure.id,
                forceClickMode: procedure.disableSampleScanning
                    || getStore().getters['variables/getFeatureFlagByName'](FeatureFlag.DISABLE_PATIENT_VISIT_SUPPLY_SCANNING),
                offlineMode: true,
                expectedSupplyItemStatus: SupplyItemStatus.ACTIVATED,
                initFooterButtonDisabled: true,
            },
        },
    });
    priorities[scanSpecimenBag.id] = 1;
    maybeAddToMap(scanSpecimenBag, workflowMap);
    /** Drop-off-samples/Present requisition form workflow item */
    const dropoffPlaceGroup = procedures === null || procedures === void 0 ? void 0 : procedures.getters.dropoffPlaceGroup(procedure);
    const headerIcon = dropoffPlaceGroup
        ? { type: 'img', value: dropoffPlaceGroup.picture }
        : undefined;
    const dropOffSamples = initWorkflowItem(DropOffWorkflowItemID.PRESENT_REQUISITION_FORM, {
        modal: {
            name: WorkflowModalComponentName.DROP_OFF_LAB_ORDER,
            headerIcon,
            title: dropoffPlaceGroup === null || dropoffPlaceGroup === void 0 ? void 0 : dropoffPlaceGroup.name,
            subtitle: 'Present digital requisition form',
            buttonProps: {
                label: 'Drop off',
                iconRight: 'chevron-right',
            },
        },
    });
    priorities[dropOffSamples.id] = 2;
    maybeAddToMap(dropOffSamples, workflowMap);
    return sortWorkflowByPriority(Array.from(workflowMap.values()), priorities);
};
/**
 * Offline supply packing workflow items
 * - Scan kit supply items
 */
const getOfflineSupplyPackingWorkflowItems = (procedure, { procedures: _ }, waypointActionID) => {
    const workflow = getCommittedWorkflow(procedure, waypointActionID, ProcedureWorkflowContextID.OFFLINE_SUPPLY_PACKING);
    const priorities = {};
    const workflowMap = existingWorkflowMap(workflow);
    /** Initialize offline supply packing procedure workflow item creator */
    const initWorkflowItem = getWorkflowItemInitializer(procedure.id, 'procedures', {
        context: ProcedureWorkflowContextID.OFFLINE_SUPPLY_PACKING,
        storeRef: 'waypointActions',
        referenceID: waypointActionID,
    });
    /** Scan kit supply items workflow item */
    const packProcedureBag = initWorkflowItem(PackingWorkflowItemID.SCAN_KIT_SUPPLY_ITEMS, {
        modal: {
            name: WorkflowModalComponentName.SCANNER,
            title: 'Scan kit supply items',
            buttonProps: {
                label: 'Scan kit supply items',
                iconRight: 'chevron-right',
            },
            props: {
                recordManagerID: 'procedures',
                recordManagerTargetID: procedure.id,
                offlineMode: procedure.disableSampleScanning,
                expectedSupplyItemStatus: SupplyItemStatus.ACTIVATED,
                initFooterButtonDisabled: true,
                actionOnScan: 'setProcedureKitSupplyItem',
            },
        },
    });
    priorities[packProcedureBag.id] = 1;
    maybeAddToMap(packProcedureBag, workflowMap);
    return sortWorkflowByPriority(Array.from(workflowMap.values()), priorities);
};
/**
 * Mail in packing workflow items
 * - Pack samples
 */
const getMailInPackingWorkflowItems = (procedure, { procedures: _ }, waypointActionID) => {
    const workflow = getCommittedWorkflow(procedure, waypointActionID, ProcedureWorkflowContextID.MAIL_IN_PACKING);
    const priorities = {};
    const workflowMap = existingWorkflowMap(workflow);
    const initWorkflowItem = getWorkflowItemInitializer(procedure.id, 'procedures', {
        context: ProcedureWorkflowContextID.MAIL_IN_PACKING,
        storeRef: 'waypointActions',
        referenceID: waypointActionID,
    });
    const packSamples = initWorkflowItem(PackingWorkflowItemID.PACK_SAMPLES, {
        component: {
            name: WorkflowInlineComponentName.TODO,
            props: {
                text: 'Pack samples',
            },
        },
    });
    priorities[packSamples.id] = 1;
    maybeAddToMap(packSamples, workflowMap);
    return sortWorkflowByPriority(Array.from(workflowMap.values()), priorities);
};
/** Main contextualized procedure workflow items */
const getProcedureWorkflow = (p, contextID = 'default', waypointActionID, recordManagers) => {
    const { procedures } = recordManagers;
    if (!procedures)
        return [];
    const procedure = extractTarget(p, procedures.getRawClone);
    if (!procedure)
        return [];
    switch (contextID) {
        case ProcedureWorkflowContextID.OFFLINE_SUPPLY_PACKING:
            return getOfflineSupplyPackingWorkflowItems(procedure, recordManagers, waypointActionID);
        case ProcedureWorkflowContextID.DROP_OFF_SAMPLES:
            return getDropOffWorkflowItems(procedure, recordManagers, waypointActionID);
        case ProcedureWorkflowContextID.MAIL_IN_PACKING:
            return getMailInPackingWorkflowItems(procedure, recordManagers, waypointActionID);
        case ProcedureWorkflowContextID.DEFAULT_FAILURE:
            return getDefaultFailureWorkflowItems(procedure, recordManagers, waypointActionID);
        default:
            return getDefaultWorkflowItems(procedure, recordManagers, waypointActionID);
    }
};

export { CHECK_PENDING_CHARTING_ITEMS_MODAL_VARIANT, getProcedureWorkflow };
